/**
 * Broadcast – Página de Transmissão WhatsApp (estilo BotConversa / ManyChat)
 * Tabs: Ativas & Agendadas | Rascunhos | Histórico
 */

import { useState, useMemo } from "react";
import { useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import { useContextoDeLista } from "@/features/assistente/contexto-de-tela";
import DashboardLayout from "@/components/DashboardLayout";
import { PermissionGuard } from "@/components/PermissionGuard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";
import {
  Plus,
  Search,
  Send,
  Users,
  CheckCircle,
  Eye,
  MousePointerClick,
  AlertCircle,
  Calendar,
  Clock,
  MoreHorizontal,
  Pause,
  Play,
  Copy,
  Trash2,
  BarChart2,
  Megaphone,
  Loader2,
  FlaskConical,
  Pencil,
  Radio,
} from "lucide-react";

// ─── Status config ──────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  draft:     { label: "Rascunho",  className: "text-gray-600 bg-gray-100 border-gray-200" },
  scheduled: { label: "Agendada",  className: "text-blue-700 bg-blue-50 border-blue-200" },
  sending:   { label: "Enviando",  className: "text-yellow-700 bg-yellow-50 border-yellow-200" },
  paused:    { label: "Pausada",   className: "text-orange-700 bg-orange-50 border-orange-200" },
  sent:      { label: "Concluída", className: "text-green-700 bg-green-50 border-green-200" },
  failed:    { label: "Falhou",    className: "text-red-700 bg-red-50 border-red-200" },
};

function StatusBadge({ status }: { status: string }) {
  const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.draft;
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${cfg.className}`}>
      {cfg.label}
    </span>
  );
}

// ─── Stats bar ──────────────────────────────────────────────────────────────

function StatsBar({ campaigns }: { campaigns: any[] }) {
  const totals = useMemo(() => {
    return campaigns.reduce(
      (acc, c) => ({
        contacts: acc.contacts + (c.totalContacts ?? 0),
        sent: acc.sent + (c.sentCount ?? 0),
        delivered: acc.delivered + (c.deliveredCount ?? 0),
        read: acc.read + (c.readCount ?? 0),
        clicked: acc.clicked + (c.clickedCount ?? 0),
        failed: acc.failed + (c.failedCount ?? 0),
      }),
      { contacts: 0, sent: 0, delivered: 0, read: 0, clicked: 0, failed: 0 }
    );
  }, [campaigns]);

  const deliveryRate = totals.sent > 0 ? Math.round((totals.delivered / totals.sent) * 100) : 0;
  const readRate = totals.delivered > 0 ? Math.round((totals.read / totals.delivered) * 100) : 0;
  const clickRate = totals.delivered > 0 ? Math.round((totals.clicked / totals.delivered) * 100) : 0;

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
      {[
        { label: "Enviadas", value: totals.sent, color: "text-blue-600", icon: Send },
        { label: "Entregues", value: totals.delivered, color: "text-green-600", icon: CheckCircle },
        { label: "Lidas", value: totals.read, color: "text-purple-600", icon: Eye },
        { label: "Clicadas", value: totals.clicked, color: "text-indigo-600", icon: MousePointerClick },
        { label: "Taxa entrega", value: `${deliveryRate}%`, color: "text-green-600", icon: CheckCircle },
        { label: "Taxa leitura", value: `${readRate}%`, color: "text-purple-600", icon: Eye },
      ].map(({ label, value, color, icon: Icon }) => (
        <Card key={label} className="px-4 py-3">
          <div className="flex items-center gap-2">
            <Icon className={`h-4 w-4 ${color}`} />
            <div>
              <div className={`text-lg font-bold ${color}`}>{value}</div>
              <div className="text-xs text-muted-foreground">{label}</div>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatDate(d: Date | string | null | undefined) {
  if (!d) return "—";
  return new Date(d).toLocaleString("pt-BR", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function pct(part: number, total: number) {
  if (total <= 0) return "0%";
  return `${Math.round((part / total) * 100)}%`;
}

// ─── Campaign row ───────────────────────────────────────────────────────────

function BroadcastCard({
  c,
  onEdit,
  onPause,
  onResume,
  onDuplicate,
  onDelete,
  onReport,
  onSend,
  onTest,
  sendingId,
}: {
  c: any;
  onEdit: (id: number) => void;
  onPause: (id: number) => void;
  onResume: (id: number) => void;
  onDuplicate: (id: number) => void;
  onDelete: (id: number) => void;
  onReport: (id: number) => void;
  onSend: (id: number, name: string) => void;
  onTest: (c: any) => void;
  sendingId: number | null;
}) {
  const total = c.totalContacts ?? 0;
  const sent = c.sentCount ?? 0;
  const delivered = c.deliveredCount ?? 0;
  const read = c.readCount ?? 0;
  const clicked = c.clickedCount ?? 0;
  const failed = c.failedCount ?? 0;

  return (
    <Card className="hover:shadow-sm transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          {/* Left: info */}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-semibold">{c.name}</p>
              <StatusBadge status={c.status} />
              {c.scheduledAt && (
                <span className="flex items-center gap-1 text-xs text-blue-600">
                  <Calendar className="h-3 w-3" />
                  {formatDate(c.scheduledAt)}
                </span>
              )}
            </div>

            {/* Metrics row */}
            <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground flex-wrap">
              <span className="flex items-center gap-1">
                <Users className="h-3.5 w-3.5" /> {total} contatos
              </span>
              {sent > 0 && (
                <>
                  <span className="flex items-center gap-1">
                    <Send className="h-3.5 w-3.5" /> {sent}/{total}
                  </span>
                  <span className="flex items-center gap-1">
                    <CheckCircle className="h-3.5 w-3.5 text-green-600" /> {pct(delivered, sent)} entregue
                  </span>
                  <span className="flex items-center gap-1">
                    <Eye className="h-3.5 w-3.5 text-purple-600" /> {pct(read, delivered)} lido
                  </span>
                  {clicked > 0 && (
                    <span className="flex items-center gap-1">
                      <MousePointerClick className="h-3.5 w-3.5 text-indigo-600" /> {pct(clicked, delivered)} clicado
                    </span>
                  )}
                  {failed > 0 && (
                    <span className="flex items-center gap-1 text-red-600">
                      <AlertCircle className="h-3.5 w-3.5" /> {failed} falhas
                    </span>
                  )}
                </>
              )}
            </div>

            {c.notes && <p className="text-xs text-muted-foreground mt-1 truncate">{c.notes}</p>}
          </div>

          {/* Right: actions */}
          <div className="flex items-center gap-1 shrink-0">
            {(c.status === "draft" || c.status === "scheduled") && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs gap-1"
                  onClick={() => onEdit(c.id)}
                >
                  <Pencil className="h-3 w-3" />
                  Editar
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs gap-1 text-green-700 border-green-300 hover:bg-green-50"
                  onClick={() => onSend(c.id, c.name)}
                  disabled={sendingId === c.id}
                >
                  {sendingId === c.id ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Play className="h-3 w-3" />
                  )}
                  Disparar
                </Button>
              </>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-7 w-7" aria-label="Mais opções">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {c.status === "sending" && (
                  <DropdownMenuItem onClick={() => onPause(c.id)}>
                    <Pause className="h-4 w-4 mr-2" /> Pausar
                  </DropdownMenuItem>
                )}
                {c.status === "paused" && (
                  <DropdownMenuItem onClick={() => onResume(c.id)}>
                    <Play className="h-4 w-4 mr-2" /> Retomar
                  </DropdownMenuItem>
                )}
                {c.templateId && (
                  <DropdownMenuItem onClick={() => onTest(c)}>
                    <FlaskConical className="h-4 w-4 mr-2" /> Enviar teste
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => onReport(c.id)}>
                  <BarChart2 className="h-4 w-4 mr-2" /> Ver relatório
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onDuplicate(c.id)}>
                  <Copy className="h-4 w-4 mr-2" /> Duplicar
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="text-destructive"
                  onClick={() => onDelete(c.id)}
                >
                  <Trash2 className="h-4 w-4 mr-2" /> Excluir
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Progress bar */}
        {total > 0 && sent > 0 && (
          <div className="mt-3">
            <div className="flex justify-between text-xs text-muted-foreground mb-1">
              <span>Progresso de entrega</span>
              <span>{pct(delivered, total)}</span>
            </div>
            <div className="h-1.5 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-primary rounded-full transition-all"
                style={{ width: `${Math.round((delivered / total) * 100)}%` }}
              />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Main page ──────────────────────────────────────────────────────────────

export default function Broadcast() {
  const [, navigate] = useLocation();
  const [activeTab, setActiveTab] = useState("active");
  const [searchQuery, setSearchQuery] = useState("");
  const [sendingId, setSendingId] = useState<number | null>(null);
  const [testModal, setTestModal] = useState<{ campaignId: number; templateId: number } | null>(null);
  const [testPhone, setTestPhone] = useState("");

  const utils = trpc.useUtils();

  // ─── Queries ──────────────────────────────────────────────────────────────
  const { data: allCampaigns = [], isLoading } = trpc.campaigns.list.useQuery();

  // ─── Mutations ────────────────────────────────────────────────────────────
  const pauseMut = trpc.campaigns.pause.useMutation({
    onSuccess: () => { utils.campaigns.list.invalidate(); toast.success("Transmissão pausada"); },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  const resumeMut = trpc.campaigns.resume.useMutation({
    onSuccess: () => { utils.campaigns.list.invalidate(); toast.success("Transmissão retomada"); },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  const duplicateMut = trpc.campaigns.duplicate.useMutation({
    onSuccess: () => { utils.campaigns.list.invalidate(); toast.success("Transmissão duplicada"); },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  const deleteMut = trpc.campaigns.delete.useMutation({
    onSuccess: () => { utils.campaigns.list.invalidate(); toast.success("Transmissão removida"); },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  const sendCampaignMut = trpc.whatsapp.sendCampaign.useMutation({
    onSuccess: (data) => {
      utils.campaigns.list.invalidate();
      setSendingId(null);
      toast.success(`Transmissão iniciada! Enviando para ${data.total} contatos...`);
    },
    onError: (e) => { setSendingId(null); toast.error(toUserFriendlyError(e)); },
  });

  const testSendMut = trpc.whatsapp.testSend.useMutation({
    onSuccess: (data) => {
      toast.success(`Teste enviado! ID: ${data.metaMessageId ?? "ok"}`);
      setTestModal(null);
      setTestPhone("");
    },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  // ─── Handlers ─────────────────────────────────────────────────────────────
  const handleSend = (id: number, name: string) => {
    if (!confirm(`Disparar a transmissão "${name}" agora?\n\nMensagens reais serão enviadas para todos os contatos segmentados.`)) return;
    setSendingId(id);
    sendCampaignMut.mutate({ campaignId: id });
  };

  const handleDelete = (id: number) => {
    if (!confirm("Remover esta transmissão?")) return;
    deleteMut.mutate({ id });
  };

  // As transmissões visíveis na aba atual, para "dispare essa" funcionar.
  useContextoDeLista({
    titulo: "Transmissões do WhatsApp",
    filtros: { aba: activeTab, busca: searchQuery || null },
    itens: (allCampaigns as any[]).map((c) => ({ id: c.id, nome: c.name, status: c.status, agendadaPara: c.scheduledAt })),
  });

  // ─── Filtering by tab ────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    let items = allCampaigns;

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      items = items.filter((c: any) => c.name?.toLowerCase().includes(q));
    }

    switch (activeTab) {
      case "active":
        return items.filter((c: any) => ["scheduled", "sending", "paused"].includes(c.status));
      case "drafts":
        return items.filter((c: any) => c.status === "draft");
      case "history":
        return items.filter((c: any) => ["sent", "failed"].includes(c.status));
      default:
        return items;
    }
  }, [allCampaigns, activeTab, searchQuery]);

  const activeCount = allCampaigns.filter((c: any) => ["scheduled", "sending", "paused"].includes(c.status)).length;
  const draftCount = allCampaigns.filter((c: any) => c.status === "draft").length;
  const historyCount = allCampaigns.filter((c: any) => ["sent", "failed"].includes(c.status)).length;

  // ─── Shared props for BroadcastCard ───────────────────────────────────────
  const cardProps = {
    onEdit: (id: number) => navigate(`/broadcast/${id}`),
    onPause: (id: number) => pauseMut.mutate({ id }),
    onResume: (id: number) => resumeMut.mutate({ id }),
    onDuplicate: (id: number) => duplicateMut.mutate({ id }),
    onDelete: handleDelete,
    onReport: (id: number) => navigate(`/broadcast/${id}/report`),
    onSend: handleSend,
    onTest: (c: any) => { setTestModal({ campaignId: c.id, templateId: c.templateId }); setTestPhone(""); },
    sendingId,
  };

  return (
    <PermissionGuard permission="whatsapp:view_broadcasts">
    <DashboardLayout>
      <div className="space-y-5">
        {/* ── Header ───────────────────────────────────────────────────────── */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Radio className="h-6 w-6" />
              Transmissão
            </h1>
            <p className="text-muted-foreground text-sm mt-0.5">
              Envie mensagens em massa via WhatsApp para seus contatos segmentados
            </p>
          </div>
          <Button onClick={() => navigate("/broadcast/create")} className="gap-2">
            <Plus className="h-4 w-4" />
            Nova Transmissão
          </Button>
        </div>

        {/* ── Stats ────────────────────────────────────────────────────────── */}
        {allCampaigns.length > 0 && (
          <StatsBar campaigns={allCampaigns.filter((c: any) => c.status === "sent")} />
        )}

        {/* ── Search ───────────────────────────────────────────────────────── */}
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar transmissão..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        {/* ── Tabs ─────────────────────────────────────────────────────────── */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="active" className="gap-1.5">
              Ativas & Agendadas
              {activeCount > 0 && <Badge variant="secondary" className="text-xs ml-1">{activeCount}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="drafts" className="gap-1.5">
              Rascunhos
              {draftCount > 0 && <Badge variant="secondary" className="text-xs ml-1">{draftCount}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="history" className="gap-1.5">
              Histórico
              {historyCount > 0 && <Badge variant="secondary" className="text-xs ml-1">{historyCount}</Badge>}
            </TabsTrigger>
          </TabsList>

          {isLoading ? (
            <div className="space-y-3 mt-4">
              {[1, 2, 3].map(i => <div key={i} className="h-24 bg-muted animate-pulse rounded-xl" />)}
            </div>
          ) : (
            <>
              <TabsContent value="active">
                {filtered.length === 0 ? (
                  <EmptyTab
                    icon={Send}
                    title="Nenhuma transmissão ativa"
                    description="Crie uma nova transmissão ou agende para um horário futuro"
                  />
                ) : (
                  <div className="space-y-3">
                    {filtered.map((c: any) => <BroadcastCard key={c.id} c={c} {...cardProps} />)}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="drafts">
                {filtered.length === 0 ? (
                  <EmptyTab
                    icon={Megaphone}
                    title="Nenhum rascunho"
                    description="Seus rascunhos de transmissão aparecerão aqui"
                  />
                ) : (
                  <div className="space-y-3">
                    {filtered.map((c: any) => <BroadcastCard key={c.id} c={c} {...cardProps} />)}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="history">
                {filtered.length === 0 ? (
                  <EmptyTab
                    icon={Clock}
                    title="Nenhuma transmissão concluída"
                    description="O histórico de transmissões aparecerá aqui após o primeiro envio"
                  />
                ) : (
                  <div className="space-y-3">
                    {filtered.map((c: any) => <BroadcastCard key={c.id} c={c} {...cardProps} />)}
                  </div>
                )}
              </TabsContent>
            </>
          )}
        </Tabs>
      </div>

      {/* ── Test send modal ────────────────────────────────────────────────── */}
      <Dialog open={!!testModal} onOpenChange={(o) => { if (!o) { setTestModal(null); setTestPhone(""); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FlaskConical className="h-4 w-4 text-amber-600" />
              Envio de Teste
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground">
              Envie uma mensagem de teste usando o template desta transmissão.
            </p>
            <div>
              <Label>Número de destino (com DDI)</Label>
              <Input
                className="mt-1"
                placeholder="5511999999999"
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value.replace(/\D/g, ""))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setTestModal(null); setTestPhone(""); }}>
              Cancelar
            </Button>
            <Button
              disabled={testPhone.length < 10 || testSendMut.isPending || !testModal?.templateId}
              onClick={() => testSendMut.mutate({ templateId: testModal!.templateId, phoneNumber: testPhone })}
            >
              {testSendMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Enviar Teste
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
    </PermissionGuard>
  );
}

// ─── Empty state ────────────────────────────────────────────────────────────

function EmptyTab({ icon: Icon, title, description }: { icon: React.ElementType; title: string; description: string }) {
  return (
    <div className="text-center py-16">
      <Icon className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
      <p className="text-muted-foreground font-medium">{title}</p>
      <p className="text-sm text-muted-foreground mt-1">{description}</p>
    </div>
  );
}
